import React, { useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import {
  Loader2,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  ClipboardCheck,
  FileEdit,
  GraduationCap,
  Plane,
  Ticket,
  Home,
  Wallet,
  Briefcase,
  ShieldCheck,
  UserPlus,
  Calendar,
  Target,
  RefreshCw
} from 'lucide-react';
import { adminAPI } from '../../services/api';
import { toast } from 'sonner';
import { format } from 'date-fns';

const SERVICES = [
  { type: 'PROFILE_ASSESSMENT', name: 'Profile Assessment', icon: ClipboardCheck, color: 'bg-blue-50 border-blue-200', iconColor: 'text-blue-600', iconBg: 'bg-blue-100' },
  { type: 'UNIVERSITY_SHORTLISTING', name: 'Preapplication Support', icon: GraduationCap, color: 'bg-green-50 border-green-200', iconColor: 'text-green-600', iconBg: 'bg-green-100' },
  { type: 'APPLICATION_ASSISTANCE', name: 'Apply University', icon: FileEdit, color: 'bg-purple-50 border-purple-200', iconColor: 'text-purple-600', iconBg: 'bg-purple-100' },
  { type: 'VISA_GUIDANCE', name: 'Visa & Interview Support', icon: Plane, color: 'bg-yellow-50 border-yellow-200', iconColor: 'text-yellow-600', iconBg: 'bg-yellow-100' },
  { type: 'SCHOLARSHIP_SEARCH', name: 'Ticket & Travel Support', icon: Briefcase, color: 'bg-cyan-50 border-cyan-200', iconColor: 'text-cyan-600', iconBg: 'bg-cyan-100' },
  { type: 'ACCOMMODATION_HELP', name: 'Find Accommodation', icon: Home, color: 'bg-pink-50 border-pink-200', iconColor: 'text-pink-600', iconBg: 'bg-pink-100' },
  { type: 'LOAN_ASSISTANCE', name: 'Education Loan', icon: Wallet, color: 'bg-emerald-50 border-emerald-200', iconColor: 'text-emerald-600', iconBg: 'bg-emerald-100' },
  { type: 'PRE_DEPARTURE_ORIENTATION', name: 'Find Jobs Abroad', icon: Ticket, color: 'bg-orange-50 border-orange-200', iconColor: 'text-orange-600', iconBg: 'bg-orange-100' },
];

const STATUS_OPTIONS = [
  { value: 'PENDING_ADMIN_ASSIGNMENT', label: 'Pending Assignment' },
  { value: 'ASSIGNED', label: 'Assigned' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'ON_HOLD', label: 'On Hold' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCELLED', label: 'Cancelled' }
];

const PRIORITY_OPTIONS = [
  { value: 'LOW', label: 'Low', color: 'bg-stone-100 text-gray-700' },
  { value: 'MEDIUM', label: 'Medium', color: 'bg-blue-100 text-blue-700' },
  { value: 'HIGH', label: 'High', color: 'bg-orange-100 text-orange-700' },
  { value: 'URGENT', label: 'Urgent', color: 'bg-red-100 text-red-700' }
];

const ApprovalStatusBadge = ({ status }) => {
  const config = {
    PENDING_APPROVAL: { icon: Clock, label: 'Pending Approval', className: 'bg-amber-100 text-amber-700 border-amber-200' },
    APPROVED: { icon: ShieldCheck, label: 'Approved', className: 'bg-green-100 text-green-700 border-green-200' },
    REJECTED: { icon: XCircle, label: 'Rejected', className: 'bg-red-100 text-red-700 border-red-200' }
  }[status];
  if (!config) return null;
  const Icon = config.icon;
  return (
    <Badge variant="outline" className={`text-xs gap-1 ${config.className}`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </Badge>
  );
};

const ServiceStatusBadge = ({ status }) => {
  const styles = {
    COMPLETED: 'bg-green-50 text-green-700 border-green-200',
    IN_PROGRESS: 'bg-blue-50 text-blue-700 border-blue-200',
    PENDING_ADMIN_ASSIGNMENT: 'bg-amber-50 text-amber-700 border-amber-200',
    ASSIGNED: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    ON_HOLD: 'bg-stone-50 text-gray-700 border-stone-200',
    CANCELLED: 'bg-red-50 text-red-700 border-red-200'
  };
  return (
    <Badge variant="outline" className={`text-xs ${styles[status] || 'bg-stone-50 text-gray-700'}`}>
      {status?.replace(/_/g, ' ')}
    </Badge>
  );
};

export default function AdminStudentServicesTab({ studentId, serviceRequests = [], counselors = [], agents = [], onUpdate }) {
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [approving, setApproving] = useState(null);

  const [editForm, setEditForm] = useState({
    status: '',
    progress: 0,
    priority: '',
    deadline: '',
    counselorId: '',
    agentId: ''
  });

  const getServiceConfig = (type) => SERVICES.find(s => s.type === type) || SERVICES[0];

  const handleEditClick = (request) => {
    setSelectedRequest(request);
    setEditForm({
      status: request.status || '',
      progress: request.progress || 0,
      priority: request.priority || 'MEDIUM',
      deadline: request.deadline ? format(new Date(request.deadline), 'yyyy-MM-dd') : '',
      counselorId: request.assignedCounselor?._id || request.assignedCounselor?.userId || '',
      agentId: request.assignedAgent?._id || request.assignedAgent?.userId || ''
    });
    setEditDialogOpen(true);
  };

  const handleApprove = async (request) => {
    setApproving(request.serviceRequestId);
    try {
      await adminAPI.approveAgentRequest(request.serviceRequestId);
      toast.success('Service request approved');
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to approve request');
    } finally {
      setApproving(null);
    }
  };

  const handleReject = async (request) => {
    setApproving(request.serviceRequestId);
    try {
      await adminAPI.rejectAgentRequest(request.serviceRequestId, 'Rejected by admin');
      toast.success('Service request rejected');
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reject request');
    } finally {
      setApproving(null);
    }
  };

  const handleSaveChanges = async () => {
    if (!selectedRequest) return;
    setUpdating(true);
    try {
      const requestId = selectedRequest.serviceRequestId || selectedRequest.requestId;

      // Update status
      if (editForm.status && editForm.status !== selectedRequest.status) {
        await adminAPI.updateServiceRequestStatus(requestId, { status: editForm.status });
      }

      // Assign if counselor or agent changed
      if (editForm.counselorId || editForm.agentId) {
        const currentCounselorId = selectedRequest.assignedCounselor?._id || selectedRequest.assignedCounselor?.userId;
        const currentAgentId = selectedRequest.assignedAgent?._id || selectedRequest.assignedAgent?.userId;

        if (editForm.counselorId !== currentCounselorId || editForm.agentId !== currentAgentId) {
          await adminAPI.assignServiceRequest(requestId, {
            counselorId: editForm.counselorId || undefined,
            agentId: editForm.agentId || undefined
          });
        }
      }

      toast.success('Service request updated successfully');
      setEditDialogOpen(false);
      onUpdate();
    } catch (error) {
      console.error('Update error:', error);
      toast.error(error.response?.data?.message || 'Failed to update service request');
    } finally {
      setUpdating(false);
    }
  };

  const appliedServiceTypes = serviceRequests.map(sr => sr.serviceType);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Service Requests</h3>
          <p className="text-sm text-gray-500">Manage and control all service requests for this student</p>
        </div>
      </div>

      {serviceRequests.length === 0 ? (
        <Card className="p-8 text-center bg-stone-50/50">
          <ClipboardCheck className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h4 className="text-gray-700 font-medium mb-1">No Services Applied</h4>
          <p className="text-sm text-gray-500">This student hasn't applied for any services yet.</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {serviceRequests.map((request) => {
            const config = getServiceConfig(request.serviceType);
            const Icon = config.icon;
            const isPendingApproval = request.isAgentInitiated && request.agentApprovalStatus === 'PENDING_APPROVAL';

            return (
              <Card key={request.serviceRequestId} className={`p-5 border ${config.color}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-xl ${config.iconBg} ${config.iconColor}`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">{config.name}</h4>
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <ServiceStatusBadge status={request.status} />
                        {request.isAgentInitiated && (
                          <ApprovalStatusBadge status={request.agentApprovalStatus} />
                        )}
                        {request.priority && (
                          <Badge className={PRIORITY_OPTIONS.find(p => p.value === request.priority)?.color || 'bg-stone-100'}>
                            <Target className="w-3 h-3 mr-1" />
                            {request.priority}
                          </Badge>
                        )}
                      </div>
                      <div className="text-sm text-gray-500 space-y-1">
                        {request.assignedCounselor && (
                          <p>Counselor: {request.assignedCounselor.firstName} {request.assignedCounselor.lastName}</p>
                        )}
                        {request.assignedAgent && (
                          <p>Agent: {request.assignedAgent.firstName} {request.assignedAgent.lastName}</p>
                        )}
                        {request.deadline && (
                          <p className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            Deadline: {format(new Date(request.deadline), 'MMM dd, yyyy')}
                          </p>
                        )}
                        {request.progress > 0 && (
                          <div className="flex items-center gap-2 mt-2">
                            <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-purple-500 rounded-full transition-all"
                                style={{ width: `${request.progress}%` }}
                              />
                            </div>
                            <span className="text-xs font-medium">{request.progress}%</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {isPendingApproval ? (
                      <>
                        <Button
                          size="sm"
                          onClick={() => handleApprove(request)}
                          disabled={approving === request.serviceRequestId}
                          className="bg-green-500 hover:bg-green-600 text-white"
                        >
                          {approving === request.serviceRequestId ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Approve
                            </>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleReject(request)}
                          disabled={approving === request.serviceRequestId}
                          className="border-red-300 text-red-600 hover:bg-red-50"
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                      </>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEditClick(request)}
                      >
                        <RefreshCw className="w-4 h-4 mr-1" />
                        Manage
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Available Services */}
      <div className="mt-8">
        <h4 className="font-medium text-gray-900 mb-4">All Available Services</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {SERVICES.map((service) => {
            const Icon = service.icon;
            const isApplied = appliedServiceTypes.includes(service.type);
            return (
              <Card
                key={service.type}
                className={`p-3 text-center ${isApplied ? 'bg-green-50/50 border-green-200' : 'bg-stone-50/50 border-stone-200'}`}
              >
                <div className={`w-10 h-10 mx-auto rounded-lg ${service.iconBg} ${service.iconColor} flex items-center justify-center mb-2`}>
                  <Icon className="w-5 h-5" />
                </div>
                <p className="text-xs font-medium text-gray-700">{service.name}</p>
                {isApplied && (
                  <Badge className="mt-2 bg-green-100 text-green-700 text-xs">Applied</Badge>
                )}
              </Card>
            );
          })}
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Manage Service Request</DialogTitle>
            <DialogDescription>
              Update status, assignment, and other details for this service request.
            </DialogDescription>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-4">
              <div className="p-3 bg-stone-50 rounded-lg">
                <p className="font-medium">{getServiceConfig(selectedRequest.serviceType).name}</p>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Status</label>
                  <Select value={editForm.status} onValueChange={(v) => setEditForm(p => ({ ...p, status: v }))}>
                    <SelectTrigger><SelectValue placeholder="Select status" /></SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Assign Counselor</label>
                  <Select value={editForm.counselorId || 'none'} onValueChange={(v) => setEditForm(p => ({ ...p, counselorId: v === 'none' ? '' : v }))}>
                    <SelectTrigger><SelectValue placeholder="Select counselor" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Counselor</SelectItem>
                      {counselors.map(c => (
                        <SelectItem key={c.userId || c._id} value={c.userId || c._id}>
                          {c.firstName} {c.lastName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Assign Agent</label>
                  <Select value={editForm.agentId || 'none'} onValueChange={(v) => setEditForm(p => ({ ...p, agentId: v === 'none' ? '' : v }))}>
                    <SelectTrigger><SelectValue placeholder="Select agent" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Agent</SelectItem>
                      {agents.map(a => (
                        <SelectItem key={a.userId || a._id} value={a.userId || a._id}>
                          {a.firstName} {a.lastName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Priority</label>
                  <Select value={editForm.priority} onValueChange={(v) => setEditForm(p => ({ ...p, priority: v }))}>
                    <SelectTrigger><SelectValue placeholder="Select priority" /></SelectTrigger>
                    <SelectContent>
                      {PRIORITY_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Deadline</label>
                  <Input
                    type="date"
                    value={editForm.deadline}
                    onChange={(e) => setEditForm(p => ({ ...p, deadline: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Progress ({editForm.progress}%)</label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={editForm.progress}
                    onChange={(e) => setEditForm(p => ({ ...p, progress: parseInt(e.target.value) }))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleSaveChanges} disabled={updating} className="bg-purple-500 hover:bg-purple-600">
                  {updating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
